/**
 * AddConnectionModal
 * ──────────────────
 * Modal that lets an admin manually connect the current case to another case.
 *
 * Wires into POST /api/graph/cases/{case_id}/connect, which idempotently
 * creates graph nodes for both sides and inserts the edge.
 */
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Search, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { searchCasesForGraph, connectCaseToCase } from '@/lib/api';

const CONNECTION_TYPES = [
  { value: 'same_location', label: 'Same Location' },
  { value: 'same_vehicle', label: 'Same Vehicle' },
  { value: 'same_dump_site', label: 'Same Dump Site' },
  { value: 'potential_match', label: 'Potential Match' },
];

// Map external case_type string → backend hint expected by /connect
const inferCaseTypeHint = (caseType) => {
  const t = (caseType || '').toLowerCase();
  if (t.includes('unidentified') || t === 'up' || t.includes('unclaimed')) return 'unidentified';
  return 'missing';
};

export const AddConnectionModal = ({
  open,
  onOpenChange,
  sourceCaseId,
  sourceCaseName,
  sourceCaseType,
  onSaved,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [connectionType, setConnectionType] = useState('same_location');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [approved, setApproved] = useState(false); // false → pending_review
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  // Reset state whenever the modal closes/opens
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setConnectionType('same_location');
      setLabel('');
      setNotes('');
      setApproved(false);
      setSaving(false);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchCasesForGraph(query.trim(), {
          excludeCaseId: sourceCaseId, limit: 10,
        });
        setResults(data.results || []);
      } catch (e) {
        console.error('case search failed', e);
        toast.error('Search failed');
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, sourceCaseId]);

  const handleSave = async () => {
    if (!selected) {
      toast.error('Pick a case to connect to first');
      return;
    }
    setSaving(true);
    try {
      await connectCaseToCase(sourceCaseId, {
        target_case_id: selected.id,
        edge_type: connectionType,
        label: label.trim() || null,
        notes: notes.trim() || null,
        status: approved ? 'approved' : 'pending_review',
        source_case_type: inferCaseTypeHint(sourceCaseType),
        target_case_type: inferCaseTypeHint(selected.case_type),
      });
      toast.success(`Connected to ${selected.name || selected.id}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Could not save connection: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="add-connection-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Add Connection from <span className="text-cyan-700 dark:text-cyan-400 truncate max-w-xs">{sourceCaseName || 'this case'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Case search */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Connect to
            </Label>
            {selected ? (
              <div className="mt-1.5 flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-cyan-300 bg-cyan-50 dark:bg-cyan-950/30">
                <div className="min-w-0">
                  <div className="font-medium truncate">{selected.name || 'Unknown'}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {selected.namus_id ? `NamUs ${selected.namus_id} · ` : ''}
                    {selected.case_type} · {selected.state || '—'}{selected.city ? ` · ${selected.city}` : ''}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setSelected(null)}
                        data-testid="add-conn-clear-selection">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative mt-1.5">
                  {searching ? (
                    <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  )}
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, NamUs ID (UP10998), or LtM UUID"
                    className="pl-9"
                    data-testid="add-conn-search-input"
                    autoFocus
                  />
                </div>
                {results.length > 0 && (
                  <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800"
                       data-testid="add-conn-results">
                    {results.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => { setSelected(r); setResults([]); setQuery(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        data-testid={`add-conn-result-${r.id}`}
                      >
                        <div className="font-medium text-sm truncate">{r.name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {r.namus_id ? `NamUs ${r.namus_id} · ` : ''}
                          {r.case_type || 'case'} · {r.state || '—'}{r.city ? ` · ${r.city}` : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {query.trim().length >= 2 && !searching && results.length === 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">No matches.</p>
                )}
              </>
            )}
          </div>

          {/* Connection type */}
          <div>
            <Label htmlFor="conn-type" className="text-xs uppercase tracking-wide text-muted-foreground">
              Connection type
            </Label>
            <Select value={connectionType} onValueChange={setConnectionType}>
              <SelectTrigger id="conn-type" className="mt-1.5" data-testid="add-conn-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONNECTION_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Label */}
          <div>
            <Label htmlFor="conn-label" className="text-xs uppercase tracking-wide text-muted-foreground">
              Label
            </Label>
            <Input
              id="conn-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='e.g. "Same Brevard Co dump site Dec 1985"'
              maxLength={200}
              className="mt-1.5"
              data-testid="add-conn-label"
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="conn-notes" className="text-xs uppercase tracking-wide text-muted-foreground">
              Notes <span className="opacity-60">(optional)</span>
            </Label>
            <Textarea
              id="conn-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Investigator context, contact info, what to verify next…"
              rows={3}
              maxLength={4000}
              className="mt-1.5"
              data-testid="add-conn-notes"
            />
          </div>

          {/* Status toggle */}
          <div className="flex items-center justify-between rounded-lg border px-3 py-2.5 bg-slate-50 dark:bg-slate-900/40">
            <div>
              <div className="text-sm font-medium">
                {approved ? 'Approved' : 'Pending Review'}
              </div>
              <div className="text-xs text-muted-foreground">
                {approved
                  ? 'Visible to the public immediately.'
                  : 'Visible only to admins until reviewed.'}
              </div>
            </div>
            <Switch checked={approved} onCheckedChange={setApproved}
                    data-testid="add-conn-status-toggle" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="add-conn-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selected || saving}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="add-conn-save">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
            ) : (
              <><Check className="w-4 h-4 mr-1" /> Save Connection</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddConnectionModal;
